import { lazy, Suspense, useMemo, useState } from 'react'
import {
  COULEUR_MODE_ZONE, creerZone, erreursZone, LIBELLE_MODE_ZONE, majZone, supprimerZone, type ModeZone, type SaisieZone, type Zone,
} from '../../api/alertes'
import { nomBoitier } from '../../api/gps'
import type { DonneesOps } from '../../api/types'
import { useDroits } from '../Contexte'
import type { Brouillon } from '../EditeurZones'
import { Icone } from '../Icone'
import { ListeDeroulante } from '../ListeDeroulante'
import { basculer, ecrire } from './AlertesOutils'

/* La carte est chargée À LA DEMANDE (comme celle du dispatch) : MapLibre pèse près d'un mégaoctet, inutile tant qu'on n'ouvre pas l'onglet. */
const EditeurZones = lazy(() => import('../EditeurZones').then((m) => ({ default: m.EditeurZones })))

/**
 * Onglet « Zones » : on dessine sur la carte (cercle : un clic place le centre ; polygone : un clic par sommet), on nomme, on choisit
 * le mode — « autorisée » (alerte à la SORTIE), « interdite » (alerte à l'ENTRÉE) ou « surveillée » (les deux) — puis les boîtiers
 * concernés. L'évaluation est faite par la base avec le même calcul (haversine / lancer de rayon). La règle « Entrée / sortie de
 * zones » (onglet Règles) fixe la gravité et les canaux ; sans elle, les zones ne déclenchent rien.
 */
interface Formulaire {
  id: string | null
  nom: string
  mode: ModeZone
  actif: boolean
  parc: boolean
  ids: string[]
  brouillon: Brouillon
}

const VIDE = (): Formulaire => ({
  id: null,
  nom: '',
  mode: 'autorisee',
  actif: true,
  parc: true,
  ids: [],
  brouillon: { forme: 'cercle', centre: null, rayonM: 300, points: [], mode: 'autorisee' },
})

export function OngletZones({
  zones,
  donnees,
  orgId,
  gere,
  surNotifier,
  surChangement,
}: {
  zones: readonly Zone[]
  donnees: DonneesOps
  orgId: string
  gere: boolean
  surNotifier: (message: string) => void
  surChangement: () => Promise<void>
}) {
  const { apercu } = useDroits()
  const [f, setF] = useState<Formulaire | null>(null)
  const [erreurs, setErreurs] = useState<string[]>([])
  const [envoi, setEnvoi] = useState(false)
  const noms = useMemo(() => new Map(donnees.boitiers.map((b) => [b.id, nomBoitier(b, donnees.vehicules)])), [donnees.boitiers, donnees.vehicules])
  const pour = { apercu, surNotifier }

  function modifier(z: Zone) {
    if (!gere) return
    setErreurs([])
    setF({
      id: z.id,
      nom: z.nom,
      mode: z.mode,
      actif: z.actif,
      parc: z.deviceIds === null,
      ids: z.deviceIds ?? [],
      brouillon: { forme: z.forme, centre: z.centre, rayonM: z.rayonM ?? 300, points: z.points ?? [], mode: z.mode },
    })
  }

  async function enregistrer() {
    if (!f) return
    const b = f.brouillon
    const pb = erreursZone({ nom: f.nom, forme: b.forme, centre: b.centre, rayonM: b.forme === 'cercle' ? b.rayonM : null, points: b.points })
    if (!f.parc && f.ids.length === 0) pb.push('Choisissez au moins un boîtier, ou « Tout le parc ».')
    setErreurs(pb)
    if (pb.length > 0) return
    const saisie: SaisieZone = {
      nom: f.nom,
      forme: b.forme,
      centre: b.centre,
      rayonM: b.forme === 'cercle' ? b.rayonM : null,
      points: b.points,
      mode: f.mode,
      actif: f.actif,
      deviceIds: f.parc ? null : f.ids,
    }
    setEnvoi(true)
    const ok = await ecrire(pour, () => (f.id ? majZone(f.id, saisie) : creerZone(orgId, saisie)), f.id ? 'Zone enregistrée' : 'Zone créée')
    setEnvoi(false)
    if (ok) {
      setF(null)
      await surChangement()
    }
  }

  async function retirer(z: Zone) {
    if (!window.confirm(`Supprimer la zone « ${z.nom} » ?\n\nSes alertes ouvertes sont refermées ; l’historique est conservé.`)) return
    const ok = await ecrire(pour, () => supprimerZone(z.id), 'Zone supprimée')
    if (!ok) return
    if (f?.id === z.id) setF(null)
    await surChangement()
  }

  async function interrupteur(z: Zone) {
    const ok = await ecrire(
      pour,
      () => majZone(z.id, { nom: z.nom, forme: z.forme, centre: z.centre, rayonM: z.rayonM, points: z.points ?? [], mode: z.mode, actif: !z.actif, deviceIds: z.deviceIds }),
      z.actif ? 'Zone désactivée' : 'Zone activée',
    )
    if (ok) await surChangement()
  }

  const changerMode = (mode: ModeZone) => f && setF({ ...f, mode, brouillon: { ...f.brouillon, mode } })

  return (
    <>
      <div className="card" data-anim>
        <div className="card-head">
          <div>
            <h3>
              <Icone nom="pin" classe="h-ic" /> Zones
            </h3>
            <div className="ch-sub">
              Dessinez Bamako et ses quartiers, nos dépôts, les zones interdites. <b>Autorisée</b> : alerte quand le véhicule en sort ; <b>interdite</b> : alerte quand
              il y entre.
            </div>
          </div>
          <span className="sp" />
          {gere && !f && (
            <button
              className="btn btn-gold btn-sm"
              onClick={() => {
                setErreurs([])
                setF(VIDE())
              }}
            >
              <Icone nom="plus" /> Nouvelle zone
            </button>
          )}
        </div>
        <div className="card-body">
          <Suspense fallback={<div className="coque-carte squelette" style={{ height: 380 }} aria-busy="true" aria-label="Chargement de la carte" />}>
            <EditeurZones
              zones={zones}
              brouillon={f?.brouillon ?? null}
              idEdite={f?.id ?? null}
              surBrouillon={(brouillon) => f && setF({ ...f, brouillon })}
              surChoisir={(z) => {
                if (!f) modifier(z)
              }}
            />
          </Suspense>
          <div className="legende-zones">
            {(Object.keys(LIBELLE_MODE_ZONE) as ModeZone[]).map((m) => (
              <span key={m}>
                <i style={{ background: COULEUR_MODE_ZONE[m] }} /> {LIBELLE_MODE_ZONE[m]}
              </span>
            ))}
          </div>

          {f && (
            <form
              className="formulaire-zone form"
              onSubmit={(e) => {
                e.preventDefault()
                void enregistrer()
              }}
            >
              {erreurs.length > 0 && (
                <div className="bandeau erreur" role="alert">
                  <ul style={{ margin: 0, paddingLeft: 18 }}>
                    {erreurs.map((e) => (
                      <li key={e}>{e}</li>
                    ))}
                  </ul>
                </div>
              )}
              <div className="form-grid">
                <div className="field form-full">
                  <label htmlFor="zone-nom">Nom de la zone</label>
                  <input id="zone-nom" value={f.nom} maxLength={80} onChange={(e) => setF({ ...f, nom: e.target.value })} placeholder="ex. Dépôt Missabougou" />
                </div>
                <div className="field">
                  <label>Forme</label>
                  <div className="subtabs" role="tablist" aria-label="Forme de la zone" style={{ marginBottom: 6 }}>
                    {(['cercle', 'polygone'] as const).map((forme) => (
                      <button
                        key={forme}
                        type="button"
                        role="tab"
                        aria-selected={f.brouillon.forme === forme}
                        className={`subtab${f.brouillon.forme === forme ? ' active' : ''}`}
                        onClick={() => setF({ ...f, brouillon: { ...f.brouillon, forme } })}
                      >
                        {forme === 'cercle' ? 'Cercle' : 'Polygone'}
                      </button>
                    ))}
                  </div>
                  <div className="t-muted">
                    {f.brouillon.forme === 'cercle'
                      ? 'Cliquez sur la carte pour placer le centre ; tirez la poignée pour le déplacer.'
                      : 'Cliquez sur la carte pour poser chaque sommet ; tirez un sommet pour le déplacer.'}
                  </div>
                </div>
                <div className="field">
                  <label>Mode</label>
                  <ListeDeroulante
                    pleineLargeur
                    valeur={f.mode}
                    surChangement={(v) => changerMode(v as ModeZone)}
                    options={(Object.keys(LIBELLE_MODE_ZONE) as ModeZone[]).map((m) => ({ valeur: m, libelle: LIBELLE_MODE_ZONE[m] }))}
                  />
                </div>
                {f.brouillon.forme === 'cercle' ? (
                  <div className="field">
                    <label htmlFor="zone-rayon">Rayon (mètres)</label>
                    <input
                      id="zone-rayon"
                      type="number"
                      min={20}
                      max={100000}
                      step={10}
                      value={Number.isFinite(f.brouillon.rayonM) ? f.brouillon.rayonM : ''}
                      onChange={(e) => setF({ ...f, brouillon: { ...f.brouillon, rayonM: e.target.value === '' ? Number.NaN : Number(e.target.value) } })}
                    />
                    <div className="t-muted">De 20 m à 100 km.</div>
                  </div>
                ) : (
                  <div className="field">
                    <label>Sommets : {f.brouillon.points.length}</label>
                    <div className="actions-ligne">
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        disabled={f.brouillon.points.length === 0}
                        onClick={() => setF({ ...f, brouillon: { ...f.brouillon, points: f.brouillon.points.slice(0, -1) } })}
                      >
                        Retirer le dernier
                      </button>
                      <button
                        type="button"
                        className="btn btn-outline btn-sm"
                        disabled={f.brouillon.points.length === 0}
                        onClick={() => setF({ ...f, brouillon: { ...f.brouillon, points: [] } })}
                      >
                        Tout effacer
                      </button>
                    </div>
                  </div>
                )}
                <fieldset className="form-full groupe-champs">
                  <legend>Boîtiers concernés</legend>
                  <label className="case-oc">
                    <input type="radio" name="zone-portee" checked={f.parc} onChange={() => setF({ ...f, parc: true })} />
                    <span>Tout le parc</span>
                  </label>
                  <label className="case-oc">
                    <input type="radio" name="zone-portee" checked={!f.parc} onChange={() => setF({ ...f, parc: false })} />
                    <span>Certains boîtiers seulement</span>
                  </label>
                  {!f.parc && (
                    <div className="choix-boitiers">
                      {[...noms.entries()].map(([id, nom]) => (
                        <label className="case-oc" key={id}>
                          <input type="checkbox" checked={f.ids.includes(id)} onChange={() => setF({ ...f, ids: basculer(f.ids, id) })} />
                          <span>{nom}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </fieldset>
                <label className="case-oc">
                  <input type="checkbox" checked={f.actif} onChange={(e) => setF({ ...f, actif: e.target.checked })} />
                  <span>Zone active</span>
                </label>
              </div>
              <div className="actions-ligne" style={{ justifyContent: 'flex-end', marginTop: 14 }}>
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setF(null)}>
                  Annuler
                </button>
                <button type="submit" className="btn btn-gold btn-sm" disabled={envoi}>
                  {envoi ? 'Enregistrement…' : f.id ? 'Enregistrer la zone' : 'Créer la zone'}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      <div className="card" data-anim>
        <div className="card-head">
          <h3>Vos zones · {zones.length}</h3>
        </div>
        <div className="card-body flush">
          {zones.length === 0 ? (
            <div className="no-hit" style={{ padding: 18 }}>
              <b>Aucune zone.</b>
              <br />
              Dessinez la première zone sur la carte (« Nouvelle zone »). Pensez aussi à activer la règle « Entrée / sortie de zones » dans l’onglet Règles.
            </div>
          ) : (
            <div className="tbl-wrap">
              <table>
                <thead>
                  <tr>
                    <th>Zone</th>
                    <th>Mode</th>
                    <th>Boîtiers</th>
                    <th>État</th>
                    {gere && <th />}
                  </tr>
                </thead>
                <tbody>
                  {zones.map((z) => (
                    <tr key={z.id}>
                      <td>
                        <div className="t-strong">
                          <span className="pastille-couleur" style={{ background: COULEUR_MODE_ZONE[z.mode] }} /> {z.nom}
                        </div>
                        <div className="t-muted">{z.forme === 'cercle' ? `Cercle, rayon ${z.rayonM} m` : `Polygone, ${z.points?.length ?? 0} sommets`}</div>
                      </td>
                      <td>{LIBELLE_MODE_ZONE[z.mode]}</td>
                      <td style={{ whiteSpace: 'normal' }}>{z.deviceIds === null ? 'Tout le parc' : z.deviceIds.map((i) => noms.get(i) ?? 'Boîtier retiré').join(', ')}</td>
                      <td>
                        <span className={`badge ${z.actif ? 'b-green' : 'b-gray'}`}>{z.actif ? 'Active' : 'Éteinte'}</span>
                      </td>
                      {gere && (
                        <td style={{ whiteSpace: 'nowrap' }}>
                          <button className="btn btn-outline btn-sm" onClick={() => modifier(z)}>
                            Modifier
                          </button>{' '}
                          <button className="btn btn-outline btn-sm" onClick={() => void interrupteur(z)}>
                            {z.actif ? 'Éteindre' : 'Activer'}
                          </button>{' '}
                          <button className="btn btn-outline btn-sm" style={{ color: 'var(--err)' }} onClick={() => void retirer(z)} aria-label={`Supprimer la zone ${z.nom}`} title="Supprimer">
                            <Icone nom="trash" />
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </>
  )
}
